import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { SEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success(t('contact.successMessage'));
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error(t('contact.errorMessage'));
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact.visitUs'),
      lines: ['1007 boul. René-Lévesque', 'Verdun (Québec) H3E 1H5'],
    },
    {
      icon: Phone,
      title: t('contact.callUs'),
      lines: ['(833) 995-6477'],
    },
    {
      icon: Mail,
      title: t('contact.writeUs'),
      lines: ['info@entrepotautomontreal.ca'],
    },
    {
      icon: Clock,
      title: t('contact.businessHours'),
      lines: [t('footer.monThu'), t('footer.friday'), t('footer.saturday'), t('footer.sunday')],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="contact-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <SEO 
        title="Contactez-nous"
        description="Contactez Entrepôt Auto Montréal au (833) 995-6477. Notre équipe est disponible pour répondre à vos questions sur nos véhicules d'occasion et options de financement. Visitez-nous au 5611 Rue Wellington, Verdun."
        url="https://entrepotautomontreal.ca/contact"
        keywords="contact concessionnaire montréal, entrepot auto telephone, achat voiture verdun, financement auto question"
      />
      {/* Hero Section with Navbar */}
      <div className="relative bg-slate-900 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?w=1920" 
            alt="" 
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-slate-900/50" />
        </div>
        
        {/* Navbar - white background */}
        <div className="relative z-10">
          <Navbar />
        </div>
        
        {/* Hero Content */}
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-10 sm:py-12 md:py-16 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-[#3b82f6] rounded-xl mb-4">
            <Mail className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
          </div>
          <h1 className="text-mobile-h1 sm:text-tablet-h1 md:text-[2.25rem] font-bold mb-3 text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {t('contact.title').split('-').length > 1 ? (
              <>{t('contact.title').split('-')[0]}<span className="text-[#3b82f6]">{t('contact.title').split('-')[1]}</span></>
            ) : (
              <>{t('contact.title')}</>
            )}
          </h1>
          <p className="text-slate-300 max-w-2xl mx-auto text-mobile-body sm:text-base">
            {t('contact.subtitle')}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-card border border-border rounded-lg p-5 sm:p-8">
              <h2 className="font-heading font-bold text-mobile-h2 sm:text-2xl mb-6">{t('contact.sendMessage')}</h2>
              
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div>
                  <Label htmlFor="name">{t('contact.name')}</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t('form.namePlaceholder')}
                    required
                    className="bg-muted mt-1.5"
                    data-testid="contact-name"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">{t('contact.email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="jean@exemple.com"
                      required
                      className="bg-muted mt-1.5"
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">{t('contact.phone')}</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="(123) 456-7890"
                      required
                      className="bg-muted mt-1.5"
                      data-testid="contact-phone"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="message">{t('contact.message')}</Label>
                  <Textarea
                    id="message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder={t('contact.messagePlaceholder')}
                    rows={5}
                    required
                    className="bg-muted mt-1.5 resize-none"
                    data-testid="contact-message"
                  />
                </div>
                <Button 
                  type="submit" 
                  size="lg"
                  className="w-full bg-primary hover:bg-primary/90"
                  disabled={submitting}
                  data-testid="contact-submit"
                >
                  {submitting ? t('contact.sending') : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      {t('contact.send')}
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {contactInfo.map((item) => (
                  <div 
                    key={item.title}
                    className="bg-card border border-border rounded-lg p-6"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-primary/10 p-3 rounded-lg">
                        <item.icon className="h-5 w-5 text-primary" />
                      </div>
                      <h3 className="font-heading font-bold text-lg">{item.title}</h3>
                    </div>
                    <div className="space-y-1">
                      {item.lines.map((line, i) => (
                        <p key={i} className="text-muted-foreground text-sm">{line}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Google Map */}
              <div className="rounded-lg overflow-hidden border border-border h-80" data-testid="google-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2797.5447!2d-73.5712!3d45.4585!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc9104e7f0b1e67%3A0x8d3e4c6a8a6e8f2b!2s1007%20Boulevard%20Ren%C3%A9-L%C3%A9vesque%2C%20Verdun%2C%20QC!5e0!3m2!1sen!2sca!4v1706300000000!5m2!1sen!2sca"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Entrepôt Auto Montréal - Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactPage;
